'use client';

import type { GuvFlatRow } from '@/new_components/guv/guvHistoryService';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  extractCostsForYear,
  getGkvCategories,
  getUkvCategories,
  isUkvStyle,
} from './costCategories';
import { createFormatters } from './currencyFormatters';

/**
 * Server-renderable data table mirroring CompanyCostsPieChart for the most
 * recent year with cost data (category / amount / share). Plain markup so the
 * breakdown lands in the SSR HTML, unlike the `ssr: false` pie chart whose
 * legend never reaches the first-wave response. Rendered behind the
 * Chart|Table toggle; the grid header leaves its right edge clear for it.
 */
export function CostsBreakdownTable({ guvData }: { guvData: GuvFlatRow[] }) {
  const { t, i18n } = useTranslation();
  const fmt = createFormatters(i18n.language);

  const compute = (): {
    year: number | null;
    rows: { label: string; value: number; pct: number }[];
  } => {
    const ukv = isUkvStyle(guvData);
    const categories = ukv ? getUkvCategories(t) : getGkvCategories(t);
    const years = Array.from(new Set(guvData.map((d) => d.year))).sort(
      (a, b) => b - a,
    );
    for (const y of years) {
      const yearData = guvData.filter((d) => d.year === y);
      const known = extractCostsForYear(yearData, categories).filter(
        (c) => c.value > 0,
      );
      const sum = known.reduce((s, c) => s + c.value, 0);
      if (sum > 0) {
        return {
          year: y,
          rows: known.map((c) => ({
            label: categories.find((cat) => cat.id === c.id)?.label ?? c.id,
            value: c.value,
            pct: (c.value / sum) * 100,
          })),
        };
      }
    }
    return { year: null, rows: [] };
  };

  const { year, rows } = compute();

  if (rows.length === 0) return null;

  return (
    <Card variant="grid">
      <CardHeader>
        <CardTitle as="h2">
          {t('companyDetail.costs.title')}
          {year ? ` (${year})` : ''}
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto pb-3">
        <table className="w-full border-collapse text-xs">
          <caption className="sr-only">
            {t('companyDetail.costs.tableCaption')}
            {year ? ` ${year}` : ''}
          </caption>
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th scope="col" className="py-1.5 pr-3 font-semibold">
                {t('companyDetail.costs.category')}
              </th>
              <th scope="col" className="py-1.5 pr-3 text-right font-semibold">
                {t('companyDetail.costs.amount')}
              </th>
              <th scope="col" className="py-1.5 text-right font-semibold">
                {t('companyDetail.costs.share')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-border/50">
                <th
                  scope="row"
                  className="py-1.5 pr-3 font-medium text-foreground"
                >
                  {r.label}
                </th>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {fmt.fullCurrency.format(r.value)}
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  {r.pct.toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
